import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Instagram, Film, Layers, PlayCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import NavbarAr from "../components/NavbarAr";
import Footer from "../components/Footer";
import FooterAr from "../components/FooterAr";
import Brand from "../components/Brand";
import { LOGO_URL } from "../lib/brand";

// The single-circle Story tile always deep-links to Instagram itself.
// When active Stories are present on Meta, we open Instagram's own Story
// viewer (which walks through every currently-active frame automatically).
// When none are active, we drop the gold ring and simply route to the
// profile — we NEVER claim there are Live Stories when Meta reports none.
const IG_STORIES_URL = "https://www.instagram.com/stories/crewznation/";
const IG_PROFILE_URL = "https://www.instagram.com/crewznation/";

const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
    en: {
        overline: "Social",
        h1title: <><Brand />'s Feed</>,
        intro: <>Follow the latest content from <Brand />'s official Instagram account</>,
        viewOnIg: "View on Instagram",
        empty: <>New content will appear here as soon as it's published on <Brand />'s official Instagram account.</>,
        pending: "The live Instagram connection is being finalized. A preview of the feed layout is shown below.",
        error: "The Instagram feed is temporarily unavailable. Please try again shortly.",
        badgeCarousel: "Carousel",
        badgeReel: "Reel",
        badgeImage: "Image",
        storiesLive: "Live Stories",
        storyTileActiveAria: "View CrewZNatioN's live Instagram Stories",
        storyTileIdleAria: "Visit CrewZNatioN on Instagram",
    },
    ar: {
        overline: "سوشل",
        h1title: <>محتوى <Brand /></>,
        intro: <>تابع أحدث محتوى من حساب <Brand /> الرسمي على إنستجرام</>,
        viewOnIg: "شاهد على إنستجرام",
        empty: <>سيظهر أحدث محتوى هنا بمجرد نشره على حساب <Brand /> الرسمي في إنستجرام.</>,
        pending: "يجري ربط حساب إنستجرام الرسمي. في الأثناء، هذا العرض التمهيدي لتصميم الصفحة.",
        error: "تعذّر تحميل المنشورات في الوقت الحالي. يُرجى المحاولة لاحقًا.",
        badgeCarousel: "كاروسيل",
        badgeReel: "ريل",
        badgeImage: "صورة",
        storiesLive: "قصص كروزنيشن",
        storyTileActiveAria: "شاهد قصص كروزنيشن المباشرة على إنستجرام",
        storyTileIdleAria: "زيارة حساب كروزنيشن على إنستجرام",
    },
};

function formatDate(iso, locale) {
    try {
        return new Intl.DateTimeFormat(locale === "ar" ? "ar-SA" : "en-GB", {
            year: "numeric", month: "short", day: "numeric",
        }).format(new Date(iso));
    } catch { return ""; }
}

function badgeFor(item, t) {
    if (item.media_type === "CAROUSEL_ALBUM") return { icon: Layers, label: t.badgeCarousel };
    if (item.is_video) return { icon: Film, label: t.badgeReel };
    return { icon: Instagram, label: t.badgeImage };
}

function MediaTile({ item, t, isAr }) {
    const B = badgeFor(item, t);
    // Guard: NEVER put a video URL into <img src>. Only image-safe URLs
    // (poster_url / thumbnail_url; and media_url only if the item is an
    // IMAGE). This eliminates the black-frame Reel tiles that occurred
    // when Meta returned a .mp4 in media_url.
    const isVideoUrl = (u) => !!u && /\.(mp4|mov|m3u8)(\?|$)/i.test(u);
    const candidates = [
        item.poster_url,
        item.thumbnail_url,
        item.media_type === "IMAGE" ? item.media_url : null,
    ].filter((u) => u && !isVideoUrl(u));
    const initial = candidates[0] || "";
    const [failed, setFailed] = React.useState(false);
    const [imgSrc, setImgSrc] = React.useState(initial);

    // Pixel-level luminance check — a "200 OK" image can still be a black
    // first-frame poster from Meta. When the img is decoded we sample the
    // canvas and if it is >92% dark pixels we walk to the next candidate;
    // if none remain, flip to the branded placeholder.
    const onImgLoad = React.useCallback((e) => {
        try {
            const img = e.currentTarget;
            const w = 24, h = 24;
            const c = document.createElement("canvas");
            c.width = w; c.height = h;
            const ctx = c.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            const data = ctx.getImageData(0, 0, w, h).data;
            let dark = 0;
            for (let i = 0; i < data.length; i += 4) {
                // Rec. 601 luminance approximation
                const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
                if (y < 18) dark++;
            }
            const ratio = dark / (w * h);
            if (ratio > 0.92) {
                // Nearly all dark pixels — treat as failed poster.
                const remaining = candidates.filter((u) => u !== imgSrc);
                if (remaining.length > 0) setImgSrc(remaining[0]);
                else setFailed(true);
            }
        } catch (_) {
            // Cross-origin canvas taint is possible; if so, we simply
            // accept the image (better to show than to falsely blank it).
        }
    }, [candidates, imgSrc]);
    return (
        <a
            href={item.permalink || "https://www.instagram.com/crewznation/"}
            target="_blank"
            rel="noreferrer noopener"
            className="group block bg-black/30 border border-white/8 hover:border-[var(--crewz-gold)]/60 transition-colors duration-300"
            data-testid="social-tile"
        >
            <div className="relative aspect-square overflow-hidden bg-neutral-900">
                {imgSrc && !failed ? (
                    <img
                        src={imgSrc}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        onLoad={onImgLoad}
                        onError={(e) => {
                            const img = e.currentTarget;
                            const remaining = candidates.filter((u) => u !== img.src);
                            if (remaining.length > 0) setImgSrc(remaining[0]);
                            else setFailed(true);
                        }}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                ) : (
                    // Truthful branded placeholder — used when Meta poster/thumb
                    // is unavailable or fails. Shows a play icon for Reels/
                    // videos and Instagram glyph for still items, plus a "View
                    // on Instagram" hint. NEVER a blank black rectangle.
                    <div
                        className="flex h-full w-full flex-col items-center justify-center gap-3 text-white/70 bg-gradient-to-br from-black/40 to-black/70"
                        data-testid="social-tile-placeholder"
                    >
                        {item.is_video ? (
                            <PlayCircle size={44} className="text-[var(--crewz-gold)]" />
                        ) : (
                            <Instagram size={40} className="text-[var(--crewz-gold)]" />
                        )}
                        <span className="text-[10px] uppercase tracking-[0.22em]">
                            {t.viewOnIg}
                        </span>
                    </div>
                )}
                <div className="absolute top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-white/90 backdrop-blur-md"
                     style={isAr ? { right: 12 } : { left: 12 }}>
                    <B.icon size={12} />
                    <span dir={isAr && (item.media_type === "CAROUSEL_ALBUM" || item.is_video) ? "rtl" : undefined}>
                        {B.label}
                    </span>
                </div>
            </div>
            <div className="p-4">
                <p className="text-[13px] leading-[1.55] text-[var(--crewz-text)]/85 line-clamp-3"
                   dir="auto">
                    {item.caption || ""}
                </p>
                <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-[var(--crewz-text)]/50">
                    <time dir="ltr">{formatDate(item.timestamp, isAr ? "ar" : "en")}</time>
                    <span className="text-[var(--crewz-gold)] group-hover:underline underline-offset-4">
                        {t.viewOnIg}
                    </span>
                </div>
            </div>
        </a>
    );
}

export default function Social({ locale = "en", embedded = false }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const Foot = isAr ? FooterAr : Footer;
    const t = isAr ? COPY.ar : COPY.en;

    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("loading");
    const [notice, setNotice] = useState(null);
    const [stories, setStories] = useState([]);
    const [storiesStatus, setStoriesStatus] = useState("loading");
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Cache-buster ensures QA sees the current live feed on every reload.
                const ts = Date.now();
                const { data } = await axios.get(`${API}/api/social/instagram?_=${ts}`);
                if (!alive) return;
                setItems(Array.isArray(data.items) ? data.items : []);
                setNextCursor(data.next_cursor || null);
                setHasMore(!!data.has_more);
                setStatus(data.source === "mock" ? "pending" : "ready");
                if (data.warning) setNotice(data.warning);
            } catch (e) {
                if (!alive) return;
                setStatus("error");
            }
            try {
                const ts = Date.now();
                const { data } = await axios.get(`${API}/api/social/instagram/stories?refresh=1&_=${ts}`);
                if (!alive) return;
                setStories(Array.isArray(data.items) ? data.items : []);
                setStoriesStatus(data.status || "ok");
            } catch (e) {
                if (!alive) return;
                setStoriesStatus("error");
            }
        })();
        return () => { alive = false; };
    }, []);

    const loadMore = useCallback(async () => {
        if (!hasMore || !nextCursor || loadingMore) return;
        setLoadingMore(true);
        try {
            const { data } = await axios.get(
                `${API}/api/social/instagram?after=${encodeURIComponent(nextCursor)}`
            );
            const newItems = Array.isArray(data.items) ? data.items : [];
            // De-dupe by id (protects against any Meta paging oddities).
            setItems((prev) => {
                const seen = new Set(prev.map((x) => x.id));
                return [...prev, ...newItems.filter((x) => !seen.has(x.id))];
            });
            setNextCursor(data.next_cursor || null);
            setHasMore(!!data.has_more);
        } catch (_e) {
            // Silent-tail failure — grid still shows what we already have.
        } finally {
            setLoadingMore(false);
        }
    }, [hasMore, nextCursor, loadingMore]);

    // Removed: multi-story modal viewer + keyboard nav. Stories are now
    // exclusively viewed on Instagram itself to guarantee the full active
    // Story queue (Meta's Graph API only returns a 7-item subset).

    // hasActiveStories: strict boolean signal from Meta — the sole trigger
    // for the gold Instagram Story ring and the /stories deep-link.
    const hasActiveStories = storiesStatus === "ok" && stories.length > 0;
    const storyHref = hasActiveStories ? IG_STORIES_URL : IG_PROFILE_URL;
    const storyAria = hasActiveStories ? t.storyTileActiveAria : t.storyTileIdleAria;

    return (
        <div dir={isAr ? "rtl" : "ltr"} id={embedded ? "social" : undefined} className={`${embedded ? "" : "min-h-screen "}bg-[var(--crewz-bg)] text-[var(--crewz-text)] ${isAr ? "font-arabic" : "font-body"}`}>
            {!embedded && <Nav />}
            <section className={`${embedded ? "pt-20 md:pt-28" : "pt-32 md:pt-40"} pb-16 md:pb-24 px-6 md:px-12 lg:px-20 max-w-6xl mx-auto`}>
                <div className="flex items-center gap-3 mb-5">
                    <span className="gold-rule" />
                    <p className="overline overline-primary">{t.overline}</p>
                </div>
                <h1
                    className={`font-display text-5xl md:text-6xl leading-[0.98] max-w-3xl text-[var(--crewz-text)]${isAr ? " !leading-[60px] md:!leading-[72px]" : ""}`}
                    data-testid="social-title"
                >
                    {t.h1title}
                </h1>
                <p className={`mt-8 max-w-2xl text-[18px] md:text-[20px] leading-[1.7] text-[var(--crewz-text)]/80 ${isAr ? "font-arabic" : "font-body"}`}
                   data-testid="social-intro">
                    {t.intro}
                </p>

                {status === "pending" && (
                    <div className="mt-8 border border-[var(--crewz-gold)]/30 bg-[var(--crewz-gold)]/5 px-5 py-4 text-[13px] text-[var(--crewz-text)]/85"
                         data-testid="social-pending">
                        {t.pending}
                    </div>
                )}
                {notice && (
                    <div className="mt-6 text-[12px] uppercase tracking-[0.2em] text-[var(--crewz-text)]/50" data-testid="social-warning">
                        {notice}
                    </div>
                )}

                {/* Single CrewZNatioN Story tile — always renders under the
                    heading. The gold Instagram Story ring appears ONLY when
                    Meta confirms at least one active Story; otherwise the
                    ring is neutral and the tile links to the profile page
                    (no false "Live" signal). Clicking the tile hands
                    navigation to Instagram, which walks through every
                    currently active Story frame automatically. */}
                <div className="mt-10" data-testid="stories-row">
                    <p className={`${isAr ? "text-[16px] font-medium" : "text-[11.5px]"} uppercase tracking-[0.24em] text-[var(--crewz-gold)] mb-4`}
                       data-testid="stories-label">
                        {t.storiesLive}
                    </p>
                    <a
                        href={storyHref}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label={storyAria}
                        className="inline-flex shrink-0 group relative focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--crewz-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--crewz-bg)] rounded-full"
                        data-testid="story-tile-single"
                        data-story-active={hasActiveStories ? "true" : "false"}
                    >
                        <span
                            className={`block h-20 w-20 md:h-24 md:w-24 rounded-full p-[2.5px] transition-transform duration-300 group-hover:scale-[1.04] ${
                                hasActiveStories
                                    ? "bg-gradient-to-br from-[var(--crewz-gold)] via-amber-300 to-[var(--crewz-gold)]"
                                    : "bg-white/15"
                            }`}
                        >
                            <span className="flex h-full w-full items-center justify-center rounded-full overflow-hidden bg-black border-2 border-[var(--crewz-bg)] p-[3px]">
                                <img
                                    src={LOGO_URL}
                                    alt="CrewZNatioN"
                                    loading="eager"
                                    draggable="false"
                                    className="h-full w-full object-contain select-none"
                                    data-testid="story-tile-avatar"
                                />
                            </span>
                        </span>
                        {hasActiveStories && (
                            <span
                                className="absolute bottom-1 h-6 w-6 md:h-7 md:w-7 rounded-full bg-[var(--crewz-bg)] flex items-center justify-center border-2 border-[var(--crewz-gold)]"
                                style={isAr ? { left: 4 } : { right: 4 }}
                                aria-hidden="true"
                                data-testid="story-tile-play-badge"
                            >
                                <PlayCircle size={14} className="text-[var(--crewz-gold)] fill-[var(--crewz-gold)]" />
                            </span>
                        )}
                    </a>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-20 pb-28 md:pb-36 max-w-6xl mx-auto">
                {status === "loading" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="social-loading">
                        {[0,1,2,3,4,5].map((i) => (
                            <div key={i} className="aspect-square bg-white/5 animate-pulse" />
                        ))}
                    </div>
                )}
                {status === "error" && (
                    <p className="text-[var(--crewz-text)]/70 text-[17px]" data-testid="social-error">{t.error}</p>
                )}
                {(status === "ready" || status === "pending") && items.length === 0 && (
                    <p className="text-[var(--crewz-text)]/70 text-[17px]" data-testid="social-empty">{t.empty}</p>
                )}
                {(status === "ready" || status === "pending") && items.length > 0 && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="social-grid">
                            {items.map((it) => (
                                <MediaTile key={it.id} item={it} t={t} isAr={isAr} />
                            ))}
                        </div>
                        {hasMore && (
                            <div className="mt-12 flex justify-center" data-testid="social-load-more-wrap">
                                <button
                                    type="button"
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    className={`inline-flex items-center gap-2 border border-[var(--crewz-gold)] text-[var(--crewz-gold)] hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3 tracking-[0.22em] uppercase font-bold transition-colors duration-300 ${isAr ? "text-[13.5px]" : "text-[12px]"}`}
                                    data-testid="social-load-more"
                                >
                                    {loadingMore
                                        ? (isAr ? "جارِ التحميل…" : "Loading…")
                                        : (isAr ? "عرض المزيد" : "Load More")}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>

            {!embedded && <Foot />}
        </div>
    );
}
